import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import type { User, InviteCode } from '../lib/types'

export default function Admin() {
  const [users, setUsers] = useState<(User & { approved: boolean; invite_code_used?: string; created_at: string; last_login?: string })[]>([])
  const [codes, setCodes] = useState<InviteCode[]>([])
  const [tab, setTab] = useState<'users' | 'codes' | 'audit'>('users')
  const [audit, setAudit] = useState<{ id: string; actor_email?: string; method: string; path: string; status: number; ip?: string; country?: string; created_at: string }[]>([])
  const [newCode, setNewCode] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [newMaxUses, setNewMaxUses] = useState(10)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    api.adminListUsers().then(setUsers).catch(() => {})
    api.adminListInviteCodes().then(setCodes).catch(() => {})
    api.adminAudit().then(setAudit).catch(() => {})
  }, [])

  async function toggleApproval(userId: string, current: boolean) {
    const updated = await api.adminApproveUser(userId, !current)
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, approved: (updated as any).approved } : u))
  }

  async function deleteUser(userId: string, email: string) {
    if (!confirm(`Delete user ${email} and all their data?`)) return
    await api.adminDeleteUser(userId)
    setUsers(prev => prev.filter(u => u.id !== userId))
  }

  async function createCode() {
    setCreating(true)
    try {
      const code = await api.adminCreateInviteCode({
        code: newCode || undefined,
        label: newLabel || undefined,
        maxUses: newMaxUses,
      })
      setCodes(prev => [code, ...prev])
      setNewCode('')
      setNewLabel('')
    } finally { setCreating(false) }
  }

  async function deactivateCode(code: string) {
    await api.adminDeactivateCode(code)
    setCodes(prev => prev.map(c => c.code === code ? { ...c, active: false } : c))
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('users')}
          className={`px-4 py-2 rounded-lg font-medium ${tab === 'users' ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Users ({users.length})
        </button>
        <button
          onClick={() => setTab('codes')}
          className={`px-4 py-2 rounded-lg font-medium ${tab === 'codes' ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Invite Codes ({codes.filter(c => c.active).length})
        </button>
        <button
          onClick={() => setTab('audit')}
          className={`px-4 py-2 rounded-lg font-medium ${tab === 'audit' ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Audit ({audit.length})
        </button>
      </div>

      {tab === 'audit' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 overflow-x-auto">
          <p className="text-xs text-gray-500 mb-3">Append-only trail of every API mutation + blocked-region attempts (governance retrofit, SPEC-AUDIT).</p>
          <table className="w-full text-xs min-w-[680px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b">
                <th className="pb-2 pr-3">When (UTC)</th><th className="pb-2 pr-3">Actor</th><th className="pb-2 pr-3">Action</th>
                <th className="pb-2 pr-3">Status</th><th className="pb-2 pr-3">IP</th><th className="pb-2">Geo</th>
              </tr>
            </thead>
            <tbody>
              {audit.map(a => (
                <tr key={a.id} className="border-b border-gray-50">
                  <td className="py-1.5 pr-3 text-gray-500 whitespace-nowrap">{a.created_at.slice(0, 19)}</td>
                  <td className="py-1.5 pr-3 text-gray-700 truncate max-w-[150px]">{a.actor_email || 'anonymous'}</td>
                  <td className="py-1.5 pr-3 font-mono text-gray-700">{a.method} {a.path}</td>
                  <td className={`py-1.5 pr-3 ${a.status < 400 ? 'text-green-600' : a.status === 451 ? 'text-amber-600' : 'text-red-600'}`}>{a.status}</td>
                  <td className="py-1.5 pr-3 text-gray-500 font-mono">{a.ip || '—'}</td>
                  <td className="py-1.5 text-gray-500">{a.country || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {audit.length === 0 && <p className="text-gray-500 text-sm py-4 text-center">No audit rows yet.</p>}
        </div>
      )}

      {tab === 'users' && (
        <div className="space-y-3">
          {users.map(u => (
            <div key={u.id} className={`border rounded-lg p-4 flex items-center justify-between ${u.approved ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div>
                <div className="font-medium">{u.email}</div>
                <div className="text-sm text-gray-500">
                  {u.name && <span className="mr-3">{u.name}</span>}
                  <span className="mr-3">Role: {u.role || 'user'}</span>
                  {u.invite_code_used && <span className="mr-3">Code: {u.invite_code_used}</span>}
                  <span>Joined: {new Date(u.created_at).toLocaleDateString()}</span>
                  {u.last_login && <span className="ml-3">Last: {new Date(u.last_login).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                {u.role !== 'admin' && (
                  <>
                    <button
                      onClick={() => toggleApproval(u.id, u.approved)}
                      className={`px-3 py-1 rounded text-sm font-medium ${
                        u.approved
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {u.approved ? 'Revoke' : 'Approve'}
                    </button>
                    <button
                      onClick={() => deleteUser(u.id, u.email)}
                      className="px-3 py-1 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      Delete
                    </button>
                  </>
                )}
                {u.role === 'admin' && (
                  <span className="px-3 py-1 rounded text-sm font-medium bg-blue-100 text-blue-700">Admin</span>
                )}
              </div>
            </div>
          ))}
          {users.length === 0 && <p className="text-gray-500">No users registered yet.</p>}
        </div>
      )}

      {tab === 'codes' && (
        <div>
          <div className="bg-gray-50 border rounded-lg p-4 mb-6">
            <h3 className="font-medium mb-3">Create Invite Code</h3>
            <div className="flex gap-3 flex-wrap">
              <input
                type="text"
                placeholder="Code (auto-generated if empty)"
                value={newCode}
                onChange={e => setNewCode(e.target.value)}
                className="border rounded px-3 py-2 text-sm flex-1 min-w-[160px]"
              />
              <input
                type="text"
                placeholder="Label (e.g. 'For team')"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                className="border rounded px-3 py-2 text-sm flex-1 min-w-[140px]"
              />
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Max uses:</label>
                <input
                  type="number"
                  value={newMaxUses}
                  onChange={e => setNewMaxUses(parseInt(e.target.value) || 1)}
                  className="border rounded px-2 py-2 text-sm w-16"
                  min={1}
                />
              </div>
              <button
                onClick={createCode}
                disabled={creating}
                className="bg-brand-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Share the invite link: <code>https://automates.istayintech.com/login?invite=CODE</code>
            </p>
          </div>

          <div className="space-y-3">
            {codes.map(c => (
              <div key={c.code} className={`border rounded-lg p-4 flex items-center justify-between ${c.active ? 'bg-white' : 'bg-gray-100 opacity-60'}`}>
                <div>
                  <div className="font-mono font-medium text-lg">{c.code}</div>
                  <div className="text-sm text-gray-500">
                    {c.label && <span className="mr-3">{c.label}</span>}
                    <span className="mr-3">Used: {c.used_count}/{c.max_uses === 0 ? 'unlimited' : c.max_uses}</span>
                    <span>{c.active ? 'Active' : 'Deactivated'}</span>
                    {c.expires_at && <span className="ml-3">Expires: {new Date(c.expires_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                {c.active && (
                  <button
                    onClick={() => deactivateCode(c.code)}
                    className="px-3 py-1 rounded text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200"
                  >
                    Deactivate
                  </button>
                )}
              </div>
            ))}
            {codes.length === 0 && <p className="text-gray-500">No invite codes created yet.</p>}
          </div>
        </div>
      )}
    </div>
  )
}
